import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';

// Nosto fills #cartpage-nosto-1 asynchronously and in more than one step, so the placement
// observer below also sees intermediate states that contain no cards at all. Asking Nosto to
// reload the placement on each of those re-enters its own render, which wipes the row and
// rebuilds it — the flicker. Cap the requests and cool them down instead; the budget resets
// on a cart change, which is the only time the recommendation set can legitimately differ.
const NOSTO_RELOAD_LIMIT = 3;
const NOSTO_RELOAD_COOLDOWN = 1000;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {MutationObserver | null} */
  #nostoObserver = null;
  /** @type {number | null} */
  #nostoRefreshFrame = null;
  /** @type {number | null} */
  #nostoArrowFrame = null;
  #nostoReloadCount = 0;
  #nostoReloadAt = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener('click', this.#handleNostoArrowClick);
    this.#observeNostoPlacement();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener('click', this.#handleNostoArrowClick);
    this.#nostoObserver?.disconnect();
    this.#nostoObserver = null;

    if (this.#nostoRefreshFrame !== null) cancelAnimationFrame(this.#nostoRefreshFrame);
    if (this.#nostoArrowFrame !== null) cancelAnimationFrame(this.#nostoArrowFrame);
    this.#nostoRefreshFrame = null;
    this.#nostoArrowFrame = null;
  }

  #handleCartAdd = (event) => {
    if (!this.hasAttribute('auto-open')) return;

    // CartAddEvent shares the "cart:update" name with other cart refresh/update events.
    // Only auto-open for true add-to-cart actions emitted by product forms.
    const source = event?.detail?.data?.source;
    const didError = event?.detail?.data?.didError;
    const isProductAdd = source === 'product-form-component' && !didError;

    if (!isProductAdd) return;

    this.showDialog();
  };

  #handleCartUpdate = () => {
    // The placement keeps its cards through the section morph (data-skip-subtree-update),
    // so nothing is requested here — Nosto decides whether the new cart warrants a
    // re-render. Only the reload budget is handed back, in case it clears the row.
    this.#nostoReloadCount = 0;
    this.#nostoReloadAt = 0;
    this.#scheduleNostoRefresh();
  };

  #handleDialogClose = () => {
    // Used by delayed refresh scripts to avoid reopening after manual close.
    this.setAttribute('data-user-closed-at', String(Date.now()));
  };

  #handleDialogOpen = () => {
    // Re-bind in case cart section morph replaced the placement node
    this.#observeNostoPlacement(true);
    // Lazy / unregistered DynamicCards need an explicit hydrate when the drawer opens
    requestAnimationFrame(() => {
      this.#loadNostoCartRecommendations();
      this.#scheduleNostoArrowState();
    });
    // Nosto may inject cards slightly after open
    setTimeout(() => {
      this.#loadNostoCartRecommendations();
      this.#scheduleNostoArrowState();
    }, 300);
    setTimeout(() => {
      this.#loadNostoCartRecommendations();
      this.#scheduleNostoArrowState();
    }, 1000);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleNostoArrowClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const root = target.closest('[data-cart-nosto-recommendations]');
    if (!root) return;

    const prevBtn = target.closest('.slideshow-control--previous');
    const nextBtn = target.closest('.slideshow-control--next');
    if (!prevBtn && !nextBtn) return;

    const scroller = root.querySelector('#cartpage-nosto-1');
    if (!(scroller instanceof HTMLElement)) return;

    event.preventDefault();

    const amount = Math.max(scroller.clientWidth * 0.8, 180);
    scroller.scrollBy({
      left: prevBtn ? -amount : amount,
      behavior: 'smooth',
    });

    // Update disabled state after scroll settles
    setTimeout(this.#scheduleNostoArrowState, 350);
  };

  // Both the placement observer and the scroll listener fire far more often than the row
  // can change, and the arrow state reads scrollWidth/clientWidth — a forced layout per
  // call. Coalesce them onto one frame.
  #scheduleNostoArrowState = () => {
    if (this.#nostoArrowFrame !== null) return;

    this.#nostoArrowFrame = requestAnimationFrame(() => {
      this.#nostoArrowFrame = null;
      this.#updateNostoArrowState();
    });
  };

  #updateNostoArrowState = () => {
    const roots = this.querySelectorAll('[data-cart-nosto-recommendations]');
    for (const root of roots) {
      const scroller = root.querySelector('#cartpage-nosto-1');
      const prevBtn = root.querySelector('.slideshow-control--previous');
      const nextBtn = root.querySelector('.slideshow-control--next');
      if (!(scroller instanceof HTMLElement) || !(prevBtn instanceof HTMLButtonElement) || !(nextBtn instanceof HTMLButtonElement)) {
        continue;
      }

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const atStart = scroller.scrollLeft <= 2;
      const atEnd = scroller.scrollLeft >= maxScroll - 2;

      prevBtn.disabled = atStart || maxScroll <= 0;
      nextBtn.disabled = atEnd || maxScroll <= 0;
    }
  };

  // Hydrating a card mutates the placement, which is what this observer watches, so the
  // callback has to be cheap and idempotent — the per-card state in
  // #loadNostoCartRecommendations is what stops it re-entering its own work.
  #scheduleNostoRefresh = () => {
    if (this.#nostoRefreshFrame !== null) return;

    this.#nostoRefreshFrame = requestAnimationFrame(() => {
      this.#nostoRefreshFrame = null;
      if (!this.querySelector('dialog')?.open) return;
      this.#loadNostoCartRecommendations();
      this.#updateNostoArrowState();
    });
  };

  /**
   * @param {boolean} [force]
   */
  #observeNostoPlacement(force = false) {
    const placement = this.querySelector('#cartpage-nosto-1');
    if (!placement) return;

    if (this.#nostoObserver) {
      if (!force) return;
      this.#nostoObserver.disconnect();
      this.#nostoObserver = null;
    }

    this.#nostoObserver = new MutationObserver(this.#scheduleNostoRefresh);
    this.#nostoObserver.observe(placement, { childList: true, subtree: true });

    if (placement instanceof HTMLElement && !placement.dataset.nostoScrollBound) {
      placement.dataset.nostoScrollBound = 'true';
      placement.addEventListener('scroll', this.#scheduleNostoArrowState, { passive: true });
    }
  }

  /**
   * Asks Nosto to (re)populate the placement, within the budget described at the top of
   * this file.
   */
  #requestNostoPlacement = () => {
    if (typeof window.nostojs !== 'function') return;
    if (this.#nostoReloadCount >= NOSTO_RELOAD_LIMIT) return;

    const now = Date.now();
    if (now - this.#nostoReloadAt < NOSTO_RELOAD_COOLDOWN) return;

    this.#nostoReloadAt = now;
    this.#nostoReloadCount += 1;
    window.nostojs((api) => {
      api?.loadRecommendations?.('cartpage-nosto-1');
    });
  };

  /**
   * Hydrate Nosto cart DynamicCards with Shopify section markup.
   * Falls back to manual Section Rendering when Nosto web components are not registered.
   */
  #loadNostoCartRecommendations = () => {
    const placement = this.querySelector('#cartpage-nosto-1');
    if (!placement) return;

    const cards = /** @type {Array<HTMLElement & { render?: () => Promise<void> }>} */ ([
      ...placement.querySelectorAll('nosto-dynamic-card'),
    ]);

    if (!cards.length) {
      this.#requestNostoPlacement();
      return;
    }

    for (const card of cards) {
      // A hydration attempt is recorded on the card itself rather than inferred from the
      // markup it ended up with. nosto-cart-recommendation-card renders nothing for a
      // product Nosto still recommends but Liquid cannot resolve, and an inferred check
      // reads that empty card as "not hydrated yet" and re-renders it on every mutation —
      // forever, since the result never changes.
      if (card.dataset.nostoHydration) continue;
      card.dataset.nostoHydration = 'pending';

      card.removeAttribute('lazy');

      // Prefer Nosto's built-in DynamicCard render when the web component is upgraded.
      // Wrapped rather than called directly so a card whose render throws synchronously,
      // or returns something other than a promise, still falls back and still settles —
      // a card left on 'pending' would never be picked up again.
      const hydration =
        typeof card.render === 'function'
          ? Promise.resolve()
              .then(() => card.render())
              .catch(() => this.#hydrateDynamicCardFromSection(card))
          : this.#hydrateDynamicCardFromSection(card);

      hydration.finally(() => {
        card.dataset.nostoHydration = 'done';
      });
    }
  };

  /**
   * @param {HTMLElement} card
   */
  #hydrateDynamicCardFromSection = async (card) => {
    const handle = card.getAttribute('handle');
    const section = card.getAttribute('section') || 'nosto-cart-recommendation-card';
    if (!handle) return;

    card.setAttribute('loading', '');
    card.removeAttribute('error');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}products/${handle}`, window.location.origin);
      url.searchParams.set('section_id', section);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const sectionEl = doc.querySelector('.shopify-section') || doc.body.firstElementChild;
      const markup = sectionEl?.innerHTML?.trim() || html.trim();

      if (!markup || /<(html|body)/i.test(markup)) {
        throw new Error('Invalid section markup');
      }

      card.innerHTML = markup;
    } catch (error) {
      console.warn('[cart-drawer] Failed to hydrate Nosto dynamic card', handle, error);
      card.setAttribute('error', '');
    } finally {
      card.removeAttribute('loading');
    }
  };

  open() {
    // Clear close marker on explicit open requests.
    this.removeAttribute('data-user-closed-at');
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
